import * as React from "react";
import { Plus } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import * as formStyles from "../../../ui/components/Form/FormInput.style.css";
import { FeatureFlagValueInput } from "../system/FeatureFlagValueInput";
import * as featureFlagsApi from "../../lib/api/feature-flags";
import type { FeatureFlag } from "../../lib/api/feature-flags";
import { useI18n } from "../../lib/i18n";

function defaultValueForFlag(flag: FeatureFlag): string {
  if (flag.type === "boolean") return "true";
  if (flag.type === "enum") return flag.options?.[0] ?? "";
  return "";
}

export function UserFeatureFlagsSection({
  userId,
  token,
  userLabel = "this user",
}: {
  userId: string;
  token: string;
  userLabel?: string;
}) {
  const { t } = useI18n();
  const [flags, setFlags] = React.useState<FeatureFlag[]>([]);
  const [overridesByFlagId, setOverridesByFlagId] = React.useState<Map<string, string>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [selectedFlagId, setSelectedFlagId] = React.useState("");
  const [draftValue, setDraftValue] = React.useState("");
  const [savingAdd, setSavingAdd] = React.useState(false);
  const [removingFlagId, setRemovingFlagId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    const [allFlags, overrides] = await Promise.all([
      featureFlagsApi.fetchFeatureFlags(token),
      featureFlagsApi.fetchUserFeatureFlagOverrides(token, userId),
    ]);
    setFlags(allFlags);
    setOverridesByFlagId(new Map(overrides.map((o) => [o.flagId, o.value])));
  }, [token, userId]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    load()
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load feature flags");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const activeOverrides = flags.filter((f) => overridesByFlagId.has(f.id));
  const availableFlagsToAdd = flags.filter((f) => f.overridable && !overridesByFlagId.has(f.id));
  const selectedFlag = flags.find((f) => f.id === selectedFlagId) ?? null;

  const startAdding = () => {
    setError(null);
    setAdding(true);
  };

  const cancelAdding = () => {
    setAdding(false);
    setSelectedFlagId("");
    setDraftValue("");
  };

  const handleAddOverride = async () => {
    if (!selectedFlagId) return;
    setSavingAdd(true);
    setError(null);
    try {
      await featureFlagsApi.upsertFeatureFlagOverride(token, selectedFlagId, userId, draftValue);
      cancelAdding();
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to add override");
    } finally {
      setSavingAdd(false);
    }
  };

  const handleRemoveOverride = async (flagId: string) => {
    setRemovingFlagId(flagId);
    setError(null);
    try {
      await featureFlagsApi.deleteFeatureFlagOverride(token, flagId, userId);
      await load();
    } catch (e: any) {
      setError(e?.message || "Failed to remove override");
    } finally {
      setRemovingFlagId(null);
    }
  };

  if (loading) {
    return (
      <Text level="body-medium" style={{ color: "var(--color-fgSecondary)" }}>
        Loading feature overrides…
      </Text>
    );
  }

  if (error && flags.length === 0) {
    return <Callout variant="error">{error}</Callout>;
  }

  if (flags.length === 0) {
    return (
      <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
        No feature flags are defined yet.
      </Text>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {error && (
        <div style={{ marginBottom: 8 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      )}

      {activeOverrides.length > 0 && (
        <>
          {!adding && availableFlagsToAdd.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
              <Button type="button" color="primary" variant="outline" iconLeft={<Plus size={14} />} onClick={startAdding}>
                Add feature override
              </Button>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: adding ? 12 : 0 }}>
            {activeOverrides.map((flag) => {
              const removing = removingFlagId === flag.id;
              return (
                <div
                  key={flag.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "10px 0",
                    borderBottom: "1px solid #F3F4F6",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text level="body-medium" weight="bold">
                      {flag.label}
                    </Text>
                    <Text level="body-small" style={{ color: "var(--color-fgSecondary)" }}>
                      <span style={{ fontFamily: "monospace" }}>{flag.key}</span> · value:{" "}
                      <span style={{ fontFamily: "monospace" }}>{overridesByFlagId.get(flag.id)}</span>
                    </Text>
                  </div>
                  <Button
                    type="button"
                    color="neutral"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleRemoveOverride(flag.id)}
                    disabled={removing}
                  >
                    {removing ? "Removing…" : "Remove override"}
                  </Button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {adding ? (
        <div
          style={{
            borderTop: activeOverrides.length > 0 ? "1px solid #E5E7EB" : "none",
            paddingTop: activeOverrides.length > 0 ? 12 : 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <span className={formStyles.label}>{t("pro.users.addOverride")}</span>
          <select
            className={formStyles.input}
            value={selectedFlagId}
            onChange={(e) => {
              const flagId = e.target.value;
              setSelectedFlagId(flagId);
              const flag = flags.find((f) => f.id === flagId);
              setDraftValue(flag ? defaultValueForFlag(flag) : "");
            }}
            disabled={savingAdd}
            autoFocus
          >
            <option value="">— Select a flag —</option>
            {availableFlagsToAdd.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label} ({f.key})
              </option>
            ))}
          </select>
          {selectedFlag && (
            <FeatureFlagValueInput
              type={selectedFlag.type}
              options={selectedFlag.options}
              value={draftValue}
              onChange={setDraftValue}
              disabled={savingAdd}
            />
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button type="button" color="neutral" variant="outline" size="sm" onClick={cancelAdding} disabled={savingAdd}>
              Cancel
            </Button>
            <Button
              type="button"
              color="primary"
              size="sm"
              onClick={() => void handleAddOverride()}
              disabled={savingAdd || !selectedFlagId}
            >
              {savingAdd ? "Saving…" : t("pro.users.addOverride")}
            </Button>
          </div>
        </div>
      ) : (
        activeOverrides.length === 0 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "16px 0" }}>
            <Text level="body-small" style={{ color: "var(--color-fgMuted)" }}>
              {userLabel} has no active feature overrides.
            </Text>
            <Button type="button" color="primary" variant="outline" iconLeft={<Plus size={14} />} onClick={startAdding}>
              Add feature override
            </Button>
          </div>
        )
      )}
    </div>
  );
}
